/**
 * /downhole/assemblies/:id/build — the Assembly Builder (fieldiq-api#883).
 * `:id` is `new` for a new assembly; `?template=<id>` preselects a template.
 *
 * THE SERVER'S ASSEMBLY IS THE ONLY COPY. Every write answers with the full
 * assembly and the hooks write it into the cache (hooks/useDownhole.ts), so the
 * string on screen is always what the API holds — positions, the recomputed
 * length, the component statuses. The unrouted builder kept a local copy
 * behind an `initialized` flag and never adopted the server's answer, so a
 * refused add or a failed reorder left the screen showing a string that did
 * not exist.
 *
 * CREATE IS EXPLICIT. A new assembly is created by its own form (name
 * required), never as a side effect of the first add. The old screen invented
 * "Untitled Assembly" to have something to POST; the API requires a name and a
 * fabricated one is a row nobody named.
 *
 * WHAT CAN BE EDITED WHEN — the API's rules, mirrored so no button is dead:
 *   string (add, insert, reorder, remove)   building only (409 otherwise)
 *   template                                building only
 *   name, customer, well, rig, hole, notes  anything but disassembled
 *   building → ready → in_hole → laid_down; ready → building is a PATCH
 * A laid-down assembly is torn down on /downhole/assemblies/:id/teardown
 * (TeardownScreen, api#905) — the Tear down button, for the build roles.
 *
 * TORQUE AND HOLE SIZE ARE STORED IMPERIAL. The inputs read in the tenant's
 * unit system and `toStoredImperial` converts before anything is sent; the
 * API is never sent metric. Torque goes in WITH the add — there is no route
 * that edits it afterwards, which is why the old screen's per-row torque box
 * (local state only, never sent) is gone.
 *
 * REMOVED, NOT FAKED: the "client tool request" form (no API field; it toasted
 * "saved" and saved nothing), the connection-type column (no such column), the
 * Assembly Sheet / Download PDF buttons (no endpoint), and the connection
 * mismatch warning (it compared the box and pin spellings of one thread as
 * strings, so it flagged correct joints).
 */
import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowDown,
  ArrowUp,
  Check,
  CircleDashed,
  FileText,
  Hash,
  Layers,
  Loader2,
  Plus,
  Rocket,
  Ruler,
  Save,
  Search,
  Trash2,
  Undo2,
  Wrench,
  X,
} from 'lucide-react';
import { api, getApiErrorMessage } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import ProvinceSelect, { CA_REGIONS, US_REGIONS, toRegionCode } from '../../components/ProvinceSelect';
import { LocaleContext } from '../../contexts/LocaleContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  useAddAssemblyComponent,
  useAssemblyTransition,
  useCreateAssembly,
  useCreateTemplate,
  useDownholeAssembly,
  useDownholeAssemblyRuns,
  useDownholeComponents,
  useDownholeTemplate,
  useDownholeTemplates,
  useDownholeTickets,
  useDeleteAssembly,
  useRemoveAssemblyComponent,
  useReorderAssembly,
  useUpdateAssembly,
} from '../../hooks/useDownhole';
import { unwrapArray } from '../../lib/api-envelope';
import { formatInstantDay } from '../../lib/calendar-date';
import { fmtCurrency, fmtQty } from '../../lib/display-labels';
import {
  DH_ASSEMBLY_STATUS_SKIN,
  DH_BHA_TYPE_LABEL,
  DH_CATEGORY_LABEL,
  DH_CONDITION_LABEL,
  dhLabel,
  dhLabelOrText,
} from '../../lib/downhole-labels';
import { canBuildDownhole } from '../../lib/downhole-roles';
import { dhUnitLabel, formatDh, toDisplayValue, toStoredImperial } from '../../lib/downhole-units';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  DH_BHA_TYPES,
  DH_COMPONENT_CATEGORIES,
  type DhAssemblyDetail,
  type DhAssemblyMember,
  type DhAssemblyWrite,
  type DhUnitSystem,
} from '../../types/downhole';
import {
  DH_INPUT,
  DH_SECONDARY,
  DhBadge,
  DhCategoryPill,
  DhDialog,
  DhEmpty,
  DhField,
  DhPanel,
  DhWearMeter,
  RunStatePill,
} from './DownholeBits';
import { RunFormDialog } from './RunDialogs';
import { lengthFromBottom, positionNote, topFirst } from './toolString';
import { TicketFormDialog, TicketStatusBadge, canReadDownholeTickets, canWriteDownholeTickets, isTicketable } from './ticketBits';

/* ─────────────────────────────── shared bits ─────────────────────────────── */

function useCustomers() {
  return useQuery({
    queryKey: ['customers'],
    queryFn: async () => unwrapArray<{ id: string; name: string }>((await api.customers.list()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/**
 * A stored inch value as the text an input starts with, in the tenant's unit:
 * mm to 0.1, inches to 0.001 — the precision each is written in. (8.752 in is
 * 222.3008 mm; at three decimals the field read 222.301, a figure nobody typed.)
 */
function diameterInput(stored: number | null | undefined, units: DhUnitSystem | null): string {
  const v = toDisplayValue(stored, 'diameter', units);
  if (v === null) return '';
  const f = units === 'metric' ? 10 : 1000;
  return String(Math.round(v * f) / f);
}

function connections(m: { connection_top: string | null; connection_bottom: string | null }): string {
  if (!m.connection_top && !m.connection_bottom) return '—';
  return `${m.connection_top || '—'} / ${m.connection_bottom || '—'}`;
}

interface DetailsDraft {
  name: string;
  customer_id: string;
  template_id: string;
  well_name: string;
  rig_name: string;
  hole_size: string;
  notes: string;
  /** Two-letter code, or '' for not set. */
  province_state: string;
}

/**
 * The province or state the assembly works in (api#910). Canada and the US
 * both have tenants, so the country is a toggle beside the list rather than a
 * guess: it starts on the stored code's own country, else the tenant's. The
 * list and its codes are ProvinceSelect's — the same CA + US set the API's
 * region check accepts — and it emits the code, never the name.
 *
 * It takes the full row: two selects side by side do not fit a half-width
 * column (measured: the list collapsed to its chevron beside the toggle).
 */
function ProvinceStateField({ value, onChange, disabled }: { value: string; onChange: (v: string) => void; disabled: boolean }) {
  // Read without throwing: outside a LocaleProvider (a test harness) the
  // toggle starts on Canada, which is the context's own default while loading.
  const isAmerican = React.useContext(LocaleContext)?.isAmerican ?? false;
  const storedCountry = value ? (US_REGIONS.some(([c]) => c === value) ? 'US' : CA_REGIONS.some(([c]) => c === value) ? 'CA' : null) : null;
  const [country, setCountry] = useState<'CA' | 'US'>(storedCountry ?? (isAmerican ? 'US' : 'CA'));
  React.useEffect(() => {
    if (storedCountry) setCountry(storedCountry);
  }, [storedCountry]);
  return (
    <div className="sm:col-span-2">
      <DhField
        label="Province / state"
        hint="Tax on this assembly's runs follows it. A run keeps the province it was logged with; blank uses the company's home province."
      >
        <div className="flex gap-2">
          <div className="w-40 shrink-0">
            <select
              className={DH_INPUT}
              aria-label="Country"
              value={country}
              onChange={(e) => {
                setCountry(e.target.value as 'CA' | 'US');
                onChange('');
              }}
              disabled={disabled}
            >
              <option value="CA">Canada</option>
              <option value="US">United States</option>
            </select>
          </div>
          <div className="min-w-0 flex-1">
            <ProvinceSelect
              value={value}
              onChange={onChange}
              country={country}
              className={DH_INPUT}
              disabled={disabled}
              aria-label="Province or state"
              placeholder={country === 'US' ? 'Not set — choose a state' : 'Not set — choose a province'}
            />
          </div>
        </div>
      </DhField>
    </div>
  );
}

/** The assembly details form, shared by create and edit. */
function DetailsFields({
  draft,
  setDraft,
  units,
  templateLocked,
  locked,
}: {
  draft: DetailsDraft;
  setDraft: (d: DetailsDraft) => void;
  units: DhUnitSystem | null;
  templateLocked: boolean;
  locked: boolean;
}) {
  const customersQuery = useCustomers();
  // Archived templates cannot seed a build (api#902, 409), so the picker
  // offers live ones only; an assembly already on an archived template keeps it
  // as "Current template" below.
  const templatesQuery = useDownholeTemplates({ active: 'true' });
  const customers = customersQuery.data ?? [];
  const templates = templatesQuery.data?.data ?? [];
  const set = (key: keyof DetailsDraft) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setDraft({ ...draft, [key]: e.target.value });

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <DhField label="Name *">
        <input className={DH_INPUT} value={draft.name} onChange={set('name')} maxLength={100} disabled={locked} required />
      </DhField>
      <DhField
        label="Customer"
        hint={queryLoadFailure(customersQuery) ? 'Customers could not be loaded; the current one is kept.' : undefined}
      >
        <select className={DH_INPUT} value={draft.customer_id} onChange={set('customer_id')} disabled={locked}>
          <option value="">No customer</option>
          {/* A select cannot show a value it has no option for: keep the
              current id selectable even before (or without) the list. */}
          {draft.customer_id && !customers.some((c) => c.id === draft.customer_id) && (
            <option value={draft.customer_id}>Current customer</option>
          )}
          {customers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </DhField>
      <DhField label="Well">
        <input className={DH_INPUT} value={draft.well_name} onChange={set('well_name')} maxLength={150} disabled={locked} />
      </DhField>
      <ProvinceStateField value={draft.province_state} onChange={(v) => setDraft({ ...draft, province_state: v })} disabled={locked} />
      <DhField label="Rig">
        <input className={DH_INPUT} value={draft.rig_name} onChange={set('rig_name')} maxLength={100} disabled={locked} />
      </DhField>
      <DhField label={`Hole size (${dhUnitLabel('diameter', units)})`}>
        <input
          className={DH_INPUT}
          type="number"
          inputMode="decimal"
          min={0}
          step="any"
          value={draft.hole_size}
          onChange={set('hole_size')}
          disabled={locked}
        />
      </DhField>
      <DhField
        label="Template"
        hint={templateLocked ? 'The template can only be changed while the assembly is building.' : undefined}
      >
        <select className={DH_INPUT} value={draft.template_id} onChange={set('template_id')} disabled={locked || templateLocked}>
          <option value="">No template</option>
          {draft.template_id && !templates.some((t) => t.id === draft.template_id) && (
            <option value={draft.template_id}>Current template</option>
          )}
          {templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </DhField>
      <div className="sm:col-span-2">
        <DhField label="Notes">
          <textarea
            className={`${DH_INPUT} h-24 py-2`}
            value={draft.notes}
            onChange={set('notes')}
            maxLength={10000}
            disabled={locked}
          />
        </DhField>
      </div>
    </div>
  );
}

/** The template's slots against the string, position by position. Display only. */
function SlotGuide({ templateId, members }: { templateId: string; members: DhAssemblyMember[] }) {
  const query = useDownholeTemplate(templateId);
  const template = query.data?.data;
  const failure = queryLoadFailure(query);
  if (query.isLoading) return null;
  if (failure) return <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="the template" />;
  if (!template) return null;
  const missingRequired = template.slots.filter((s) => {
    const m = members.find((x) => x.position === s.slot_number);
    return s.required && (!m || m.category !== s.category);
  }).length;
  return (
    <div>
      {/* Top first, like the string beside it: slot 1 is position 1, the bottom. */}
      <ul className="divide-y divide-fieldiq-border" aria-label={`${template.name} slots, top of the string first`}>
        {[...template.slots].sort((x, y) => y.slot_number - x.slot_number).map((slot) => {
          const m = members.find((x) => x.position === slot.slot_number);
          const fits = !!m && m.category === slot.category;
          return (
            <li key={slot.slot_number} className="flex items-center gap-3 px-4 py-2.5 text-sm">
              <span className="w-6 text-right font-mono text-fieldiq-muted">{slot.slot_number}</span>
              {fits ? (
                <Check className="size-4 shrink-0 text-fieldiq-success" aria-label="Filled" />
              ) : m ? (
                <X className="size-4 shrink-0 text-fieldiq-danger" aria-label="Different category" />
              ) : (
                <CircleDashed className="size-4 shrink-0 text-fieldiq-muted" aria-label="Empty" />
              )}
              <span className="min-w-0 flex-1">
                <span className="font-bold text-white">{slot.label || dhLabel(DH_CATEGORY_LABEL, slot.category)}</span>
                <span className="ml-2 text-xs text-fieldiq-muted">
                  {dhLabel(DH_CATEGORY_LABEL, slot.category)}
                  {slot.required ? ' · required' : ''}
                </span>
                {m && !fits && (
                  <span className="block text-xs text-fieldiq-text">
                    Position {slot.slot_number} holds a {dhLabel(DH_CATEGORY_LABEL, m.category).toLowerCase()}
                  </span>
                )}
              </span>
              {m && <span className="font-mono text-xs text-fieldiq-text">{m.serial_number}</span>}
            </li>
          );
        })}
      </ul>
      {missingRequired > 0 && (
        <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-text">
          {missingRequired} required {missingRequired === 1 ? 'slot is' : 'slots are'} not filled by the right category. This is a
          guide; the API does not block Ready on it.
        </p>
      )}
    </div>
  );
}

/* ─────────────────────────────── new assembly ────────────────────────────── */

function NewAssembly({ templateParam }: { templateParam: string | null }) {
  const navigate = useNavigate();
  const create = useCreateAssembly();
  const templatesQuery = useDownholeTemplates();
  const templateQuery = useDownholeTemplate(templateParam);
  const units = templatesQuery.data?.meta.unit_system ?? null;
  const [draft, setDraft] = useState<DetailsDraft>({
    name: '',
    customer_id: '',
    template_id: templateParam ?? '',
    well_name: '',
    rig_name: '',
    hole_size: '',
    notes: '',
    province_state: '',
  });
  const [prefilled, setPrefilled] = useState(false);

  // ?template= — fill the empty fields from the template once it loads. The
  // reader's own entries always win; this only fills blanks.
  const template = templateQuery.data?.data;
  if (template && !prefilled) {
    setPrefilled(true);
    setDraft((d) => ({
      ...d,
      template_id: template.id,
      customer_id: d.customer_id || template.customer_id || '',
      hole_size: d.hole_size || diameterInput(template.hole_size, templateQuery.data?.meta.unit_system ?? units),
      name: d.name || template.name,
    }));
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const name = draft.name.trim();
    if (!name) {
      toast.error('Name the assembly first.');
      return;
    }
    const body: DhAssemblyWrite = {
      name,
      customer_id: draft.customer_id || null,
      template_id: draft.template_id || null,
      well_name: draft.well_name.trim() || null,
      rig_name: draft.rig_name.trim() || null,
      hole_size: toStoredImperial(draft.hole_size, 'diameter', units, 3),
      notes: draft.notes.trim() || null,
      province_state: draft.province_state || null,
    };
    try {
      const result = await create.mutateAsync(body);
      if (!result.data) throw new Error('The API returned no assembly');
      toast.success(`${result.data.assembly_number} created`);
      navigate(`/downhole/assemblies/${result.data.id}/build`, { replace: true });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'The assembly could not be created.'));
    }
  };

  return (
    <div data-testid="downhole-builder-new" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow="Assembly builder" title="New assembly" onBack={() => navigate(-1)} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto grid max-w-[1920px] gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <DhPanel eyebrow="Step 1 of 2" title="Assembly details">
            <form onSubmit={submit} className="space-y-4 p-4">
              <DetailsFields draft={draft} setDraft={setDraft} units={units} templateLocked={false} locked={create.isPending} />
              <div className="flex justify-end">
                <button type="submit" className="fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50" disabled={create.isPending}>
                  {create.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Plus className="size-4" aria-hidden="true" />}
                  Create and build
                </button>
              </div>
            </form>
          </DhPanel>
          <DhPanel eyebrow="Step 2 of 2" title="Make up the string">
            <p className="px-4 py-4 text-sm text-fieldiq-text">
              Once the assembly exists you add available components to it, in order, with their make-up torque.
              {draft.template_id ? ' The template’s slots are shown beside the string as a guide.' : ''}
            </p>
          </DhPanel>
        </div>
      </section>
    </div>
  );
}

/* ──────────────────────────────── workbench ──────────────────────────────── */

function draftFrom(a: DhAssemblyDetail, units: DhUnitSystem | null): DetailsDraft {
  return {
    name: a.name ?? '',
    customer_id: a.customer_id ?? '',
    template_id: a.template_id ?? '',
    well_name: a.well_name ?? '',
    rig_name: a.rig_name ?? '',
    hole_size: diameterInput(a.hole_size, units),
    notes: a.notes ?? '',
    province_state: toRegionCode(a.province_state),
  };
}

/**
 * Only the fields the reader changed. Sending every field would write a blank
 * over a value this screen never showed (memory: post only touched fields), and
 * `null` — not `undefined` — is how a field is cleared: undefined means "not
 * supplied" to the API and would silently keep the old value.
 */
function changedFields(a: DhAssemblyDetail, d: DetailsDraft, units: DhUnitSystem | null): DhAssemblyWrite {
  const body: DhAssemblyWrite = {};
  const text = (v: string) => v.trim() || null;
  if (d.name.trim() !== a.name) body.name = d.name.trim();
  if ((d.customer_id || null) !== (a.customer_id ?? null)) body.customer_id = d.customer_id || null;
  if ((d.template_id || null) !== (a.template_id ?? null)) body.template_id = d.template_id || null;
  if (text(d.well_name) !== (a.well_name ?? null)) body.well_name = text(d.well_name);
  if (text(d.rig_name) !== (a.rig_name ?? null)) body.rig_name = text(d.rig_name);
  if (text(d.notes) !== (a.notes ?? null)) body.notes = text(d.notes);
  if ((d.province_state || null) !== (toRegionCode(a.province_state) || null)) body.province_state = d.province_state || null;
  const hole = toStoredImperial(d.hole_size, 'diameter', units, 3);
  const was = a.hole_size == null ? null : Math.round(a.hole_size * 1000) / 1000;
  if (hole !== was && d.hole_size !== diameterInput(a.hole_size, units)) body.hole_size = hole;
  return body;
}

function Workbench({ id }: { id: string }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canBuild = canBuildDownhole(user?.role);
  const canReadTickets = canReadDownholeTickets(user?.role);
  const query = useDownholeAssembly(id);
  const failure = queryLoadFailure(query);
  const asm = query.data?.data ?? null;
  const meta = query.data?.meta;
  const units = meta?.unit_system ?? null;
  const tz = meta?.time_zone ?? null;

  const update = useUpdateAssembly();
  const transition = useAssemblyTransition();
  const add = useAddAssemblyComponent();
  const reorder = useReorderAssembly();
  const remove = useRemoveAssemblyComponent();
  const busy = update.isPending || transition.isPending || add.isPending || reorder.isPending || remove.isPending;

  const [draft, setDraft] = useState<DetailsDraft | null>(null);
  const [confirm, setConfirm] = useState<null | 'dispatch' | 'return'>(null);
  const [deleting, setDeleting] = useState(false);
  const deleteAssembly = useDeleteAssembly();
  const [templateOpen, setTemplateOpen] = useState(false);

  // The form starts from the server's assembly and resets whenever the server's
  // version moves on (a save, a transition) — keyed on updated_at.
  const [draftFor, setDraftFor] = useState<string | null>(null);
  if (asm && draftFor !== asm.updated_at) {
    setDraftFor(asm.updated_at);
    setDraft(draftFrom(asm, units));
  }

  if (query.isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center bg-fieldiq-dark py-24">
        <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading assembly" />
      </div>
    );
  }
  // A failed BACKGROUND refetch keeps the assembly on screen: swapping the page
  // for the failure notice would unmount every open form and dialog under the
  // reader (a run half-typed, a template being named). Only a read that never
  // produced an assembly gets the notice in its place; a stale one is flagged
  // in the status strip below.
  if (!asm || !draft) {
    return (
      <div className="min-h-full bg-fieldiq-dark">
        <PageHeaderBand eyebrow="Assembly builder" title="Assembly" onBack={() => navigate(-1)} backLabel="Back" />
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)]">
          <div className="mx-auto max-w-[1920px] rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
            {failure ? (
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this assembly" />
            ) : (
              <DhEmpty>This assembly does not exist in your register.</DhEmpty>
            )}
          </div>
        </section>
      </div>
    );
  }

  const building = asm.status === 'building';
  const editableDetails = asm.status !== 'disassembled';
  const members = asm.components;
  const changes = changedFields(asm, draft, units);
  const dirty = Object.keys(changes).length > 0;

  const run = async (label: string, fn: () => Promise<unknown>) => {
    try {
      await fn();
      toast.success(label);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'The change was not saved.'));
    }
  };

  const saveDetails = () => {
    if (!draft.name.trim()) {
      toast.error('An assembly needs a name.');
      return;
    }
    run('Details saved', () => update.mutateAsync({ id, body: changes }));
  };

  // `members` in position order (1 = the bottom). `dir` +1 moves a tool UP the
  // string (to a higher position), -1 down toward the bit.
  const byPosition = [...members].sort((a, b) => a.position - b.position);
  const move = (componentId: string, dir: -1 | 1) => {
    const next = [...byPosition];
    const index = next.findIndex((x) => x.component_id === componentId);
    if (index < 0 || index + dir < 0 || index + dir >= next.length) return;
    const [m] = next.splice(index, 1);
    next.splice(index + dir, 0, m);
    run('String reordered', () =>
      reorder.mutateAsync({ id, order: next.map((x, i) => ({ component_id: x.component_id, position: i + 1 })) }),
    );
  };
  const fromBottom = lengthFromBottom(members);

  const primaryAction =
    asm.status === 'building'
      ? { label: 'Mark ready', icon: <Check className="size-4" aria-hidden="true" />, onClick: () => run('Marked ready', () => transition.mutateAsync({ id, action: 'ready' })), disabled: members.length === 0 }
      : asm.status === 'ready'
        ? { label: 'Dispatch', icon: <Rocket className="size-4" aria-hidden="true" />, onClick: () => setConfirm('dispatch'), disabled: false }
        : asm.status === 'in_hole'
          ? { label: 'Return', icon: <Undo2 className="size-4" aria-hidden="true" />, onClick: () => setConfirm('return'), disabled: false }
          : asm.status === 'laid_down' && canBuild
            ? { label: 'Tear down', icon: <Wrench className="size-4" aria-hidden="true" />, onClick: () => navigate(`/downhole/assemblies/${asm.id}/teardown`), disabled: false }
            : null;

  const statusNote: Record<string, string> = {
    building: 'Being made up. Add, insert, reorder and remove components, then mark it ready.',
    ready: 'Built. The string is fixed; send it back to building to change it, or dispatch it to the well.',
    in_hole: 'Running at the well. Log its runs below; close an open run before returning it.',
    laid_down: 'Back from the well and awaiting teardown. Runs can still be logged until it is torn down.',
    disassembled: 'Torn down. This is the record of what was in it.',
  };

  return (
    <div data-testid="downhole-builder" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={asm.assembly_number}
        title={asm.name}
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          primaryAction && (
            <button
              type="button"
              onClick={primaryAction.onClick}
              disabled={busy || primaryAction.disabled}
              className="fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50"
            >
              {primaryAction.icon}
              {primaryAction.label}
            </button>
          )
        }
        stats={[
          { icon: <Layers className="size-4" aria-hidden="true" />, label: 'Tools', sub: 'In the string', tone: 'neutral', value: fmtQty(asm.component_count) },
          { icon: <Ruler className="size-4" aria-hidden="true" />, label: 'Length', sub: 'Made-up string', tone: 'info', value: formatDh(asm.total_length_ft, 'length', units) },
          { icon: <Hash className="size-4" aria-hidden="true" />, label: 'Runs', sub: 'Logged', tone: 'neutral', value: fmtQty(asm.run_count) },
          { icon: <CircleDashed className="size-4" aria-hidden="true" />, label: 'Hole size', sub: 'Planned', tone: 'accent', value: formatDh(asm.hole_size, 'diameter', units) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="flex flex-wrap items-center gap-3 rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3">
            <DhBadge map={DH_ASSEMBLY_STATUS_SKIN} value={asm.status} />
            <p className="min-w-0 flex-1 basis-56 text-sm text-fieldiq-text">{statusNote[asm.status] ?? '—'}</p>
            {failure && (
              <p role="status" className="w-full text-xs text-fieldiq-muted">
                This assembly could not be refreshed just now, so it shows what loaded earlier. It refreshes on its own when the connection is back.
              </p>
            )}
            {asm.status === 'ready' && (
              <button
                type="button"
                className={DH_SECONDARY}
                disabled={busy}
                onClick={() => run('Back to building', () => update.mutateAsync({ id, body: { status: 'building' } }))}
              >
                <Undo2 className="size-4" aria-hidden="true" />
                Back to building
              </button>
            )}
            {members.length > 0 && (
              <button type="button" className={DH_SECONDARY} disabled={busy} onClick={() => setTemplateOpen(true)}>
                <FileText className="size-4" aria-hidden="true" />
                Save as template
              </button>
            )}
            {/* api#902: only a building assembly with no runs can be deleted; past
                building it has history and is torn down, not deleted. */}
            {canBuild && building && (asm.run_count ?? 0) === 0 && (
              <button type="button" className={DH_SECONDARY} disabled={busy} onClick={() => setDeleting(true)}>
                <Trash2 className="size-4" aria-hidden="true" />
                Delete assembly
              </button>
            )}
          </div>

          <div className="grid gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <div className="min-w-0 space-y-4">
              <DhPanel
                eyebrow={building ? 'Top of the string first · position 1 is the bottom' : 'As made up · top first, position 1 at the bottom'}
                title="Tool string"
                testId="downhole-tool-string"
                footer={
                  members.length > 0
                    ? `${members.length} ${members.length === 1 ? 'tool' : 'tools'} · ${
                        asm.total_length_ft == null
                          ? 'length — (a tool in the string has no length on record)'
                          : `${formatDh(asm.total_length_ft, 'length', units)} long`
                      }${asm.built_at ? ` · built ${formatInstantDay(asm.built_at, tz) ?? '—'}${asm.built_by_name ? ` by ${asm.built_by_name}` : ''}` : ''}`
                    : undefined
                }
              >
                {members.length === 0 ? (
                  <DhEmpty>
                    {building ? 'The string is empty. Add components from the list beside it.' : 'This assembly has no components in it now.'}
                  </DhEmpty>
                ) : (
                  <ol className="divide-y divide-fieldiq-border" aria-label="Tool string, top of the string first">
                    {topFirst(members).map((m) => (
                      <li key={m.assembly_component_id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                        <span className="flex w-12 shrink-0 flex-col items-center">
                          <span className="grid size-8 place-items-center rounded-full bg-fi-amber/15 font-mono text-sm font-extrabold text-fieldiq-text">
                            {m.position}
                          </span>
                          {positionNote(m.position, members.length) && (
                            <span className="mt-0.5 text-[0.6rem] font-black uppercase tracking-[0.08em] text-fieldiq-muted">
                              {positionNote(m.position, members.length)}
                            </span>
                          )}
                        </span>
                        <div className="min-w-0 flex-1 basis-56">
                          <Link
                            to={`/downhole/components/${m.component_id}`}
                            className="font-mono text-sm font-extrabold text-fieldiq-accent-signal hover:underline"
                          >
                            {m.serial_number}
                          </Link>
                          <p className="truncate text-xs text-fieldiq-muted">{m.description || '—'}</p>
                          <p className="mt-1 text-xs text-fieldiq-text">{connections(m)}</p>
                        </div>
                        <DhCategoryPill category={m.category} />
                        <dl className="flex gap-4 text-xs">
                          <div>
                            <dt className="text-fieldiq-muted">OD</dt>
                            <dd className="font-mono text-fieldiq-text">{formatDh(m.od, 'diameter', units)}</dd>
                          </div>
                          <div>
                            <dt className="text-fieldiq-muted">Length</dt>
                            <dd className="font-mono text-fieldiq-text">{formatDh(m.length_ft, 'length', units)}</dd>
                          </div>
                          <div>
                            <dt className="text-fieldiq-muted">From bottom</dt>
                            <dd className="font-mono text-fieldiq-text">{formatDh(fromBottom.get(m.assembly_component_id) ?? null, 'length', units)}</dd>
                          </div>
                          <div>
                            <dt className="text-fieldiq-muted">Make-up</dt>
                            <dd className="font-mono text-fieldiq-text">{formatDh(m.makeup_torque, 'torque', units)}</dd>
                          </div>
                        </dl>
                        <DhWearMeter percent={m.wear_percent_hours} label="Wear used, of the hours limit" />
                        {building && (
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              className="rounded-fi-control p-1.5 text-fieldiq-muted transition hover:bg-fieldiq-dark hover:text-white disabled:opacity-30"
                              aria-label={`Move ${m.serial_number} up the string`}
                              disabled={busy || m.position === members.length}
                              onClick={() => move(m.component_id, 1)}
                            >
                              <ArrowUp className="size-4" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="rounded-fi-control p-1.5 text-fieldiq-muted transition hover:bg-fieldiq-dark hover:text-white disabled:opacity-30"
                              aria-label={`Move ${m.serial_number} down the string`}
                              disabled={busy || m.position === 1}
                              onClick={() => move(m.component_id, -1)}
                            >
                              <ArrowDown className="size-4" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="rounded-fi-control p-1.5 text-fieldiq-muted transition hover:bg-fieldiq-dark hover:text-white disabled:opacity-30"
                              aria-label={`Remove ${m.serial_number}`}
                              disabled={busy}
                              onClick={() =>
                                run(`${m.serial_number} removed`, () => remove.mutateAsync({ id, componentId: m.component_id }))
                              }
                            >
                              <Trash2 className="size-4" aria-hidden="true" />
                            </button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ol>
                )}
              </DhPanel>

              {asm.removed_components.length > 0 && (
                <DhPanel eyebrow="Teardown" title="Removed from this assembly">
                  <ul className="divide-y divide-fieldiq-border">
                    {asm.removed_components.map((m) => (
                      <li key={m.assembly_component_id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm">
                        <span className="w-6 text-right font-mono text-fieldiq-muted">{m.position}</span>
                        <Link to={`/downhole/components/${m.component_id}`} className="font-mono font-extrabold text-fieldiq-accent-signal hover:underline">
                          {m.serial_number}
                        </Link>
                        <DhCategoryPill category={m.category} />
                        <span className="text-fieldiq-text">Condition: {dhLabelOrText(DH_CONDITION_LABEL, m.condition_on_return)}</span>
                        <span className="text-fieldiq-muted">Removed {formatInstantDay(m.removed_at, tz) ?? '—'}</span>
                      </li>
                    ))}
                  </ul>
                </DhPanel>
              )}

              {building && <Picker id={id} asm={asm} units={units} busy={busy} onAdd={add.mutateAsync} run={run} />}

              {((asm.run_count ?? 0) > 0 || asm.status === 'in_hole' || asm.status === 'laid_down') && (
                <AssemblyRuns asm={asm} units={units} tz={tz} canLog={canBuild && (asm.status === 'in_hole' || asm.status === 'laid_down')} />
              )}

              {canReadTickets && (asm.run_count ?? 0) > 0 && <AssemblyTickets asm={asm} tz={tz} />}
            </div>

            <div className="min-w-0 space-y-4">
              {asm.template_id && (
                <DhPanel eyebrow={`Template · ${asm.template_name ?? '—'}`} title="Slot guide">
                  {/* A torn-down assembly's string is empty; its record is what was removed. */}
                  <SlotGuide
                    templateId={asm.template_id}
                    members={asm.status === 'disassembled' ? asm.removed_components : members}
                  />
                </DhPanel>
              )}
              <DhPanel eyebrow="Assembly" title="Details">
                <form
                  className="space-y-4 p-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    saveDetails();
                  }}
                >
                  <DetailsFields
                    draft={draft}
                    setDraft={setDraft}
                    units={units}
                    templateLocked={!building}
                    locked={!editableDetails || busy}
                  />
                  {editableDetails && (
                    <div className="flex justify-end gap-2">
                      <button type="button" className={DH_SECONDARY} disabled={!dirty || busy} onClick={() => setDraft(draftFrom(asm, units))}>
                        Discard
                      </button>
                      <button type="submit" className="fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50" disabled={!dirty || busy}>
                        <Save className="size-4" aria-hidden="true" />
                        Save details
                      </button>
                    </div>
                  )}
                </form>
              </DhPanel>
            </div>
          </div>
        </div>
      </section>

      <DhDialog
        open={confirm !== null}
        title={confirm === 'dispatch' ? `Dispatch ${asm.assembly_number}?` : `Return ${asm.assembly_number}?`}
        onClose={() => setConfirm(null)}
        footer={
          <>
            <button type="button" className={DH_SECONDARY} onClick={() => setConfirm(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50"
              disabled={busy}
              onClick={async () => {
                const action = confirm;
                setConfirm(null);
                if (action) await run(action === 'dispatch' ? 'Dispatched' : 'Returned', () => transition.mutateAsync({ id, action }));
              }}
            >
              {confirm === 'dispatch' ? 'Dispatch' : 'Return'}
            </button>
          </>
        }
      >
        <p className="text-sm text-fieldiq-text">
          {confirm === 'dispatch'
            ? `The assembly goes in the hole and its ${members.length} ${members.length === 1 ? 'component moves' : 'components move'} to In hole. The string can no longer be changed.`
            : `The assembly is laid down and its components move back to In assembly until it is torn down.`}
        </p>
      </DhDialog>

      <SaveTemplateDialog open={templateOpen} onClose={() => setTemplateOpen(false)} asm={asm} />

      <DhDialog
        open={deleting}
        title={`Delete ${asm.assembly_number}?`}
        onClose={() => setDeleting(false)}
        footer={
          <>
            <button type="button" className={DH_SECONDARY} onClick={() => setDeleting(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex h-9 items-center gap-1.5 rounded-fi-control border border-black/20 bg-fi-signal-red px-3 text-sm font-bold text-fi-signal-ink transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={deleteAssembly.isPending}
              onClick={async () => {
                try {
                  await deleteAssembly.mutateAsync(id);
                  toast.success(`${asm.assembly_number} deleted`);
                  setDeleting(false);
                  navigate('/downhole/assemblies');
                } catch (error) {
                  toast.error(getApiErrorMessage(error, 'The assembly was not deleted.'));
                }
              }}
            >
              {deleteAssembly.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
              Delete assembly
            </button>
          </>
        }
      >
        <p className="text-sm text-fieldiq-text">
          {members.length > 0
            ? `${asm.name} is removed and its ${members.length} ${members.length === 1 ? 'tool goes' : 'tools go'} back to Available. `
            : `${asm.name} is removed. `}
          This cannot be undone.
        </p>
      </DhDialog>
    </div>
  );
}

/**
 * The runs this assembly has made, and the way to log one (api#902).
 *
 * Log Run is offered while the assembly is in the hole (a run can be opened,
 * logged complete, or recorded as mobilized-but-not-run) and once it is laid
 * down (complete or not-run only — the API opens a run only on an in-hole
 * string). Each run links to the run log, where it is closed, edited or
 * deleted.
 */
function AssemblyRuns({ asm, units, tz, canLog }: { asm: DhAssemblyDetail; units: DhUnitSystem | null; tz: string | null; canLog: boolean }) {
  const query = useDownholeAssemblyRuns(asm.id);
  const failure = queryLoadFailure(query);
  const runs = query.data?.data ?? [];
  const [logging, setLogging] = useState(false);
  // Log run waits for the runs, and is not offered while one is open — the
  // field shell's rule: an open run is closed next, not joined by another
  // (the API opens one run at a time and refuses the rest).
  const openRun = runs.find((r) => r.run_state === 'open') ?? null;
  const offerLog = canLog && !!query.data && !openRun;
  return (
    <DhPanel
      eyebrow="History"
      title="Runs"
      footer={
        canLog && openRun ? (
          <>
            Run {openRun.run_number ?? '—'} is still in the hole.{' '}
            <Link to={`/downhole/run-logs/${openRun.id}`} className="font-bold text-fieldiq-accent-signal underline">
              Close it on the run log
            </Link>{' '}
            before logging another.
          </>
        ) : undefined
      }
      actions={
        offerLog ? (
          <button type="button" className={DH_SECONDARY} onClick={() => setLogging(true)}>
            <Plus className="size-4" aria-hidden="true" />
            Log run
          </button>
        ) : undefined
      }
    >
      {query.isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="size-5 animate-spin text-fieldiq-muted" aria-label="Loading runs" />
        </div>
      ) : failure && !query.data ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this assembly's runs" />
      ) : runs.length === 0 ? (
        <DhEmpty>No runs have been logged for this assembly.</DhEmpty>
      ) : (
        <ul className="divide-y divide-fieldiq-border">
          {runs.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm">
              <Link to={`/downhole/run-logs/${r.id}`} className="font-mono font-extrabold text-fieldiq-accent-signal hover:underline">
                Run {r.run_number ?? '—'}
              </Link>
              <RunStatePill state={r.run_state} />
              <span className="text-fieldiq-text">In {formatInstantDay(r.run_in_at, tz) ?? '—'}</span>
              <span className="text-fieldiq-text">Out {formatInstantDay(r.run_out_at, tz) ?? '—'}</span>
              <span className="font-mono text-fieldiq-text">{formatDh(r.footage_drilled, 'length', units, { digits: 0 })}</span>
            </li>
          ))}
        </ul>
      )}
      {canLog && (
        <RunFormDialog open={logging} onClose={() => setLogging(false)} assembly={asm} units={units} tz={tz} />
      )}
    </DhPanel>
  );
}

/**
 * The assembly's field tickets (0461) and the way to make one: a ticket covers
 * runs of THIS assembly that are closed (or mobilized-not-run), on no ticket
 * and not already invoiced. "Need a ticket" counts those runs from the runs
 * read — the same one the Runs panel shows — so the panel and the dialog
 * cannot disagree about which runs are free.
 */
function AssemblyTickets({ asm, tz }: { asm: DhAssemblyDetail; tz: string | null }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = canWriteDownholeTickets(user?.role);
  const ticketsQuery = useDownholeTickets({ assembly_id: asm.id });
  const runsQuery = useDownholeAssemblyRuns(asm.id);
  const failure = queryLoadFailure(ticketsQuery);
  const tickets = ticketsQuery.data?.data ?? [];
  const runs = runsQuery.data?.data ?? [];
  const free = runs.filter(isTicketable);
  const [creating, setCreating] = useState(false);
  return (
    <DhPanel
      eyebrow="Customer sign-off"
      title="Field tickets"
      testId="downhole-assembly-tickets"
      actions={
        canWrite && runsQuery.data && free.length > 0 ? (
          <button type="button" className={DH_SECONDARY} onClick={() => setCreating(true)}>
            <Plus className="size-4" aria-hidden="true" />
            New ticket
          </button>
        ) : undefined
      }
      footer={
        runsQuery.data
          ? free.length > 0
            ? `${free.length} ${free.length === 1 ? 'run needs' : 'runs need'} a ticket before ${free.length === 1 ? 'it' : 'they'} can be invoiced: run ${free.map((r) => r.run_number ?? '—').join(', ')}.`
            : undefined
          : undefined
      }
    >
      {ticketsQuery.isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="size-5 animate-spin text-fieldiq-muted" aria-label="Loading tickets" />
        </div>
      ) : failure && !ticketsQuery.data ? (
        <LoadFailureNotice error={queryFailureError(ticketsQuery)} kind={failure} subject="this assembly's tickets" />
      ) : tickets.length === 0 ? (
        <DhEmpty>No field ticket has been made for this assembly.</DhEmpty>
      ) : (
        <ul className="divide-y divide-fieldiq-border">
          {tickets.map((t) => (
            <li key={t.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm">
              <Link to={`/downhole/tickets/${t.id}`} className="font-mono font-extrabold text-fieldiq-accent-signal hover:underline">
                {t.ticket_number}
              </Link>
              <TicketStatusBadge status={t.status} />
              <span className="text-fieldiq-text">{t.run_numbers.length ? `Run ${t.run_numbers.join(', ')}` : '—'}</span>
              <span className="font-mono text-fieldiq-text">{fmtCurrency(t.total_amount, t.currency)}</span>
              {t.status === 'customer_rejected' && t.customer_rejection_reason && (
                <span className="basis-full text-xs text-fieldiq-text">Rejected: “{t.customer_rejection_reason}”</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {canWrite && (
        <TicketFormDialog
          open={creating}
          onClose={() => setCreating(false)}
          assemblyId={asm.id}
          assemblyNumber={asm.assembly_number}
          runs={runs}
          runsLoaded={!!runsQuery.data}
          tz={tz}
          onSaved={(ticketId) => navigate(`/downhole/tickets/${ticketId}`)}
        />
      )}
    </DhPanel>
  );
}

/** Available, active components, added at a position with an optional make-up torque. */
function Picker({
  id,
  asm,
  units,
  busy,
  onAdd,
  run,
}: {
  id: string;
  asm: DhAssemblyDetail;
  units: DhUnitSystem | null;
  busy: boolean;
  onAdd: ReturnType<typeof useAddAssemblyComponent>['mutateAsync'];
  run: (label: string, fn: () => Promise<unknown>) => Promise<void>;
}) {
  const query = useDownholeComponents({ status: 'available' });
  const failure = queryLoadFailure(query);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [position, setPosition] = useState('');
  const [torque, setTorque] = useState('');
  const n = asm.components.length;

  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (query.data?.data ?? []).filter(
      (c) =>
        // The API refuses an inactive component (409); do not offer one.
        c.is_active &&
        (!category || c.category === category) &&
        (!q || c.serial_number.toLowerCase().includes(q) || (c.description ?? '').toLowerCase().includes(q)),
    );
  }, [query.data, search, category]);

  const torqueUnit = dhUnitLabel('torque', units);
  const torqueInvalid = torque.trim() !== '' && toStoredImperial(torque, 'torque', units) === null;

  const addOne = (componentId: string, serial: string) => {
    if (torqueInvalid) {
      toast.error('Make-up torque must be a number.');
      return;
    }
    const at = position ? Number(position) : undefined;
    run(`${serial} added${at ? ` at position ${at}` : ''}`, async () => {
      await onAdd({
        id,
        component_id: componentId,
        ...(at ? { position: at } : {}),
        // Converted to ft·lbs here; never sent in the reader's unit.
        makeup_torque: toStoredImperial(torque, 'torque', units),
      });
      setTorque('');
      setPosition('');
    });
  };

  return (
    <DhPanel eyebrow="Add to the string" title="Available components" testId="downhole-picker">
      <div className="grid gap-3 border-b border-fieldiq-border p-4 sm:grid-cols-2 lg:grid-cols-4">
        <label className="relative sm:col-span-2">
          <span className="sr-only">Search serial number or description</span>
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
          <input
            type="search"
            className={`${DH_INPUT} pl-9`}
            placeholder="Search serial number or description"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <select className={DH_INPUT} aria-label="Filter by category" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">All categories</option>
          {DH_COMPONENT_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {DH_CATEGORY_LABEL[c]}
            </option>
          ))}
        </select>
        <select className={DH_INPUT} aria-label="Insert at position" value={position} onChange={(e) => setPosition(e.target.value)}>
          <option value="">{n === 0 ? 'Position 1, the bottom' : `Add above the last tool (position ${n + 1})`}</option>
          {/* Top first, like the string. Inserting at p puts the tool at p and
              moves the one there (and everything above it) up one. */}
          {topFirst(asm.components).map((m) => (
            <option key={m.position} value={m.position}>
              Insert at position {m.position}, below {m.serial_number}
              {m.position === 1 ? ' (the bottom)' : ''}
            </option>
          ))}
        </select>
        <div className="sm:col-span-2 lg:col-span-4">
          <DhField label={`Make-up torque for the next add (${torqueUnit})`} hint="Optional. It is recorded with the add and cannot be edited afterwards.">
            <input
              className={`${DH_INPUT} sm:max-w-xs`}
              type="number"
              inputMode="decimal"
              min={0}
              step="any"
              value={torque}
              onChange={(e) => setTorque(e.target.value)}
              aria-invalid={torqueInvalid}
            />
          </DhField>
        </div>
      </div>
      {query.isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading available components" />
        </div>
      ) : failure ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="available components" />
      ) : list.length === 0 ? (
        <DhEmpty>{search || category ? 'No available component matches.' : 'Every component is in use, in the shop or retired.'}</DhEmpty>
      ) : (
        <ul className="max-h-[28rem] divide-y divide-fieldiq-border overflow-y-auto">
          {list.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
              <div className="min-w-0 flex-1 basis-56">
                <p className="font-mono text-sm font-extrabold text-white">{c.serial_number}</p>
                <p className="truncate text-xs text-fieldiq-muted">{c.description || '—'}</p>
              </div>
              <DhCategoryPill category={c.category} />
              <span className="font-mono text-xs text-fieldiq-text">OD {formatDh(c.od, 'diameter', units)}</span>
              <DhWearMeter percent={c.wear_percent_hours} label="Wear used, of the hours limit" />
              <button type="button" className={DH_SECONDARY} disabled={busy} onClick={() => addOne(c.id, c.serial_number)} aria-label={`Add ${c.serial_number}`}>
                <Plus className="size-4" aria-hidden="true" />
                Add
              </button>
            </li>
          ))}
        </ul>
      )}
    </DhPanel>
  );
}

/**
 * The string as a template: one slot per component, by position, with its
 * category. Slots carry category, label and required only — no OD, no
 * connection (the template table has no such columns).
 */
function SaveTemplateDialog({ open, onClose, asm }: { open: boolean; onClose: () => void; asm: DhAssemblyDetail }) {
  const create = useCreateTemplate();
  const [name, setName] = useState('');
  const [bhaType, setBhaType] = useState('');
  const [required, setRequired] = useState(true);

  const submit = async () => {
    if (!name.trim() || !bhaType) {
      toast.error('A template needs a name and a BHA type.');
      return;
    }
    try {
      await create.mutateAsync({
        name: name.trim(),
        bha_type: bhaType,
        customer_id: asm.customer_id,
        hole_size: asm.hole_size,
        slots: asm.components.map((m) => ({
          slot_number: m.position,
          category: m.category,
          label: m.description ? m.description.slice(0, 100) : null,
          required,
        })),
      });
      toast.success(`Template “${name.trim()}” saved`);
      setName('');
      setBhaType('');
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'The template was not saved.'));
    }
  };

  return (
    <DhDialog
      open={open}
      title="Save the string as a template"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50" disabled={create.isPending} onClick={submit}>
            Save template
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <DhField label="Template name *">
          <input className={DH_INPUT} value={name} onChange={(e) => setName(e.target.value)} maxLength={100} />
        </DhField>
        <DhField label="BHA type *">
          <select className={DH_INPUT} value={bhaType} onChange={(e) => setBhaType(e.target.value)}>
            <option value="">Choose…</option>
            {DH_BHA_TYPES.map((t) => (
              <option key={t} value={t}>
                {DH_BHA_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </DhField>
        <label className="flex items-center gap-2 text-sm text-fieldiq-text">
          <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
          Mark every slot required
        </label>
        <ol className="space-y-1 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark p-3 text-xs text-fieldiq-text">
          {asm.components.map((m) => (
            <li key={m.assembly_component_id}>
              <span className="font-mono text-fieldiq-muted">{m.position}.</span> {dhLabel(DH_CATEGORY_LABEL, m.category)}
              {m.description ? ` — ${m.description}` : ''}
            </li>
          ))}
        </ol>
        <p className="text-xs text-fieldiq-muted">Templates cannot be edited or deleted once saved.</p>
      </div>
    </DhDialog>
  );
}

export default function AssemblyBuilderScreen() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  if (!id || id === 'new') return <NewAssembly templateParam={searchParams.get('template')} />;
  return <Workbench key={id} id={id} />;
}

